import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import { formatKpiValue, sanitizeKpiHref } from './kpiData';
import { readmodelDisplayCount, type ReadmodelWidgetState } from './readmodelState';
import { ReadmodelEmptyHint, ReadmodelLoading, ReadmodelUnavailable } from './ReadmodelWidgetStates';
import { WidgetLink } from './WidgetLink';
import classes from './CountWidget.module.css';

/**
 * TV1-2291 (SDD §5/§6 · t25) — the `count` tile.
 *
 * **`KeyIndicators` is literally this widget.** Two of its four tiles were migrated
 * to Cube-backed `kpi` widgets in TV1-1936; the remaining two ("Publicaciones",
 * "Productos") stayed hand-written because the widget grammar had no way to say
 * *"a count from a readmodel"*. It does now, and this renders what `MetricCard`
 * renders: a title (drawn by the card chrome, NOT here — see `WidgetCard`), a big
 * number, and an optional caption under it.
 *
 * ## Why it is not simply `KpiWidget`
 *
 * A KPI's number is a Cube MEASURE and can legitimately be *unknown* — that is the
 * entire subject of `kpiData.ts`, and it is why a KPI renders an em-dash. A count
 * cannot be unknown: the query either failed (an error tile, upstream) or it ran and
 * counted rows. So this tile has no `sumMeasure`, no `null` value and no em-dash —
 * and, correspondingly, no delta: a readmodel query has no prior window to compare
 * against, which is exactly why `count` is a separate type rather than a `kpi` prop.
 *
 * It DOES share the KPI's formatter (`formatKpiValue`) and its type scale, because
 * the two sit side by side in a headline row and any divergence would read as a
 * defect. One formatter, not two.
 *
 * ## Zero is an answer, not an absence
 *
 * `state.kind === 'empty'` still renders `0` — the predicate was evaluated against
 * every row and nothing matched, which is a MEASUREMENT (see `readmodelState.ts`).
 * The `ReadmodelEmptyHint` line beside it is what stops a bare `0` from being
 * ambiguous between "nothing matched" and "this widget is broken"; the genuinely
 * broken case renders {@link ReadmodelUnavailable} instead, and never a number.
 */

export type CountWidgetProps = {
  /** The resolved state — see `readmodelState.resolveReadmodelWidgetState`. */
  state: ReadmodelWidgetState;
  /** Optional label under the number (the card chrome already renders the title). */
  caption?: string;
  /**
   * 🔴 UNTRUSTED, and allow-listed by `sanitizeKpiHref` (2026-08-19, review item 4:
   * "count widget should be able to have a link too").
   *
   * ⚠️ READ THE C5 BLOCK IN `AlertWidget` BEFORE TOUCHING THIS. Widget layouts are
   * SHARED account-scoped documents, so an authored `javascript:` URI here is a
   * PERSISTED, CROSS-USER stored-XSS sink — the exact defect TV1-1934 was, on
   * `kpi.href`. The schema does NOT protect it (`S.string()` accepts
   * `javascript:` and there is a negative test proving so); the renderer is the
   * boundary, and it FAILS CLOSED: an unlisted scheme renders plain text with no
   * anchor at all, never a dead `<a>` and never a raw fallback.
   *
   * One sanitiser for all three tiles (`kpi`, `alert`, `count`) — a second would
   * drift from it silently.
   */
  href?: string;
  /**
   * 🔗 THE LINK GATE. `true` renders the number as plain text even when `href` is
   * valid: in the editor a card is an object being ARRANGED, and a click that
   * navigates away abandons an unsaved layout. Same rule as `kpi` and `alert`.
   */
  editMode?: boolean;
  /** Explicit locale for deterministic tests; defaults to the runtime locale. */
  locale?: string;
};

export const CountWidget = ({ state, caption, href, editMode = false, locale }: CountWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( state.kind === 'loading' ) return <ReadmodelLoading />;
  // `error` is normally intercepted by `WebWidget`'s error tile before it reaches a
  // parser. Handled anyway so the component is correct on its own terms — and so a
  // failed fetch can never surface as a number.
  if ( state.kind === 'error' || state.kind === 'unavailable' ) return <ReadmodelUnavailable />;

  const count = readmodelDisplayCount( state );

  // 🔴 C5. `editMode` short-circuits BEFORE the sanitiser, so an author's click can
  // never navigate regardless of how good the href is; `sanitizeKpiHref` returns
  // `null` for anything off the scheme allow-list, and `null` renders PLAIN TEXT
  // WITH NO ANCHOR. There is deliberately no `else` branch that reaches `href` again.
  const linkTarget = editMode ? null : sanitizeKpiHref( href );

  const valueBody = (
    <Text
      component="div"
      className={classes.value}
      data-testid={componentsTestIds.countWidgetValue}
      title={state.kind === 'empty'
        ? t( 'widget_readmodel_empty_hint', 'Nothing matches right now' )
        : undefined}
    >
      {/* `decimals: 0` — a row count is an integer by construction; letting the
          shared formatter's default 2 fraction digits through would render
          "1.234" for 1234 in a locale that groups with a dot. */}
      {formatKpiValue( count, 'number', { locale, decimals: 0 })}
    </Text>
  );

  /*
   * ⚠️ THE WHOLE TILE IS THE LINK, not just the number (2026-08-19), on the ticket
   * owner's call — see `WidgetLink`. "38 publicaciones" is one statement; clicking
   * any part of it should take you to the 38.
   */
  return (
    <WidgetLink
      target={linkTarget}
      testId={componentsTestIds.countWidgetLink}
      className={classes.link}
    >
    <div className={classes.container} data-testid={componentsTestIds.countWidget} data-readmodel-state={state.kind}>
      {/*
        ⚠️ THE SUBJECT ICON IS NOT HERE ANY MORE (2026-08-19). `WidgetCard` draws it
        on the title's row — see the note in `AlertWidget`. From inside the card BODY
        it could never rise above the title, which is exactly the alignment the fixed
        `KeyIndicators` tiles have and these were asked to match.
      */}

      {valueBody}

      {state.kind === 'empty' ? <ReadmodelEmptyHint /> : null}

      {/*
        ⚠️ SUPPRESSED IN AN EMPTY STATE (2026-08-19, review item 2). An empty tile was
        showing the measured `0`, the "nothing matches" hint AND the caption — more
        text than a populated tile carries, for a card with nothing to report. The
        hint IS the empty state; the caption is context for a reading that is absent.
      */}
      {caption && state.kind === 'ready' ? (
        <Text component="div" className={classes.caption} data-testid={componentsTestIds.countWidgetCaption}>
          {caption}
        </Text>
      ) : null}
    </div>
    </WidgetLink>
  );
};
